import { join, map, or, regexp, seq, token } from '@st/util/tspc'
import { Decimal, type Decimal as DecimalValue } from './decimal'

export type CalculatorExpression =
  | { op: AddSubtractOperator; children: CalculatorExpression[] }
  | { op: MultiplyDivideOperator; children: CalculatorExpression[] }
  | { op: 'number'; value: string }

type AddSubtractOperator = '+' | '-'
type MultiplyDivideOperator = '*' | '/'
export const CALCULATOR_DISPLAY_SCALE = 2

// Helper parser that always succeeds with an empty array
// Used to make a sequence of operations optional (zeroOrMore).
const succeedEmptyArray =
  <T>() =>
  (input: string, position: number): { success: true; value: T[]; position: number } => ({
    success: true,
    value: [] as T[],
    position
  })

const numberToken = map(
  regexp(/-?([0-9]+(\.[0-9]*)?|\.[0-9]+)/),
  (s: string): CalculatorExpression => ({ op: 'number', value: s })
)

// Factor handles multiplication and division
const mulDivOperatorToken = or<string, MultiplyDivideOperator>(token('*'), token('/'))
const mulDivOpTermPairParser = seq(mulDivOperatorToken, numberToken)
const zeroOrMoreMulDivOpTermPairs = or(
  join(mulDivOpTermPairParser, token('')), // token('') as separator means pairs are just concatenated
  succeedEmptyArray<[MultiplyDivideOperator, CalculatorExpression]>()
)
const factorToken = map(
  seq(numberToken, zeroOrMoreMulDivOpTermPairs),
  ([firstTerm, subsequentOpTermPairs]) => {
    if (subsequentOpTermPairs.length === 0) {
      return firstTerm
    }

    // Try to form an n-ary multiplication node
    const firstOperator = subsequentOpTermPairs[0][0]
    if (firstOperator === '*') {
      const allMultiply = subsequentOpTermPairs.every(([op, _]) => op === '*')
      if (allMultiply) {
        const children = [firstTerm]
        for (const [_op, term] of subsequentOpTermPairs) {
          children.push(term)
        }
        return { op: '*', children } as CalculatorExpression
      }
    }

    // Default to left-associative binary operations for division or mixed operations
    return subsequentOpTermPairs.reduce(
      (acc, [op, val]) => ({ op, children: [acc, val] }) as CalculatorExpression,
      firstTerm
    )
  }
)

// Expression handles addition and subtraction
const addSubOperatorToken = or<string, AddSubtractOperator>(token('+'), token('-'))
const addSubOpFactorPairParser = seq(addSubOperatorToken, factorToken)
const zeroOrMoreAddSubOpFactorPairs = or(
  join(addSubOpFactorPairParser, token('')),
  succeedEmptyArray<[AddSubtractOperator, CalculatorExpression]>()
)
const exprToken = map(
  seq(factorToken, zeroOrMoreAddSubOpFactorPairs),
  ([firstFactor, subsequentOpFactorPairs]) => {
    if (subsequentOpFactorPairs.length === 0) {
      return firstFactor
    }

    // Try to form an n-ary addition node
    const firstOperator = subsequentOpFactorPairs[0][0]
    if (firstOperator === '+') {
      const allAddition = subsequentOpFactorPairs.every(([op, _]) => op === '+')
      if (allAddition) {
        const children = [firstFactor]
        for (const [_op, factor] of subsequentOpFactorPairs) {
          children.push(factor)
        }
        return { op: '+', children } as CalculatorExpression
      }
    }

    // Default to left-associative binary operations for subtraction or mixed operations
    return subsequentOpFactorPairs.reduce(
      (acc, [op, val]) => ({ op, children: [acc, val] }) as CalculatorExpression,
      firstFactor
    )
  }
)

export const CalculatorExpression = {
  parse: (expr: string): CalculatorExpression | undefined => {
    if (expr.length === 0) return undefined
    // Remove all whitespace to simplify parsing, as operators are single characters
    const cleanedExpr = expr.replace(/\s+/g, '')
    const res = exprToken(cleanedExpr, 0)
    // Ensure the entire string was consumed
    return res.success && res.position === cleanedExpr.length ? res.value : undefined
  },
  eval: (expr: CalculatorExpression | string): string | undefined => {
    const result = CalculatorExpression.evalExact(expr)

    if (!result) {
      return undefined
    }
    return Decimal.formatFixed(result, CALCULATOR_DISPLAY_SCALE)
  },
  evalExact: (expr: CalculatorExpression | string): DecimalValue | undefined => {
    const resolvedExpr = typeof expr === 'string' ? CalculatorExpression.parse(expr) : expr
    if (!resolvedExpr) {
      return undefined
    }

    return evalMathExpression(resolvedExpr)
  },
  validate: (expr: CalculatorExpression | string): boolean => {
    return CalculatorExpression.evalExact(expr) != undefined
  }
}

function evalMathExpression(expr: CalculatorExpression): DecimalValue | undefined {
  switch (expr.op) {
    case 'number':
      return Decimal.parse(expr.value)
    case '+': {
      if (expr.children.length == 0) return undefined
      let sum = Decimal.ZERO
      for (const child of expr.children) {
        const childValue = evalMathExpression(child)
        if (!childValue) return undefined
        sum = Decimal.add(sum, childValue)
      }
      return sum
    }
    case '-': {
      if (expr.children.length != 2) return undefined
      const leftValue = evalMathExpression(expr.children[0])
      const rightValue = evalMathExpression(expr.children[1])
      if (!leftValue || !rightValue) return undefined
      return Decimal.subtract(leftValue, rightValue)
    }
    case '*': {
      if (expr.children.length == 0) return undefined
      let result = evalMathExpression(expr.children[0])
      if (!result) return undefined
      for (let i = 1; i < expr.children.length; i++) {
        const childValue = evalMathExpression(expr.children[i])
        if (!childValue) return undefined
        result = Decimal.multiply(result, childValue)
      }
      return result
    }
    case '/': {
      if (expr.children.length != 2) return undefined
      const leftValue = evalMathExpression(expr.children[0])
      const rightValue = evalMathExpression(expr.children[1])
      if (!leftValue || !rightValue) return undefined
      return Decimal.divide(leftValue, rightValue)
    }
  }
}
